import { Injectable } from '@angular/core'
import {
  _filterNullishValues,
  _last,
  _numberEnumKeyOrUndefined,
  _objectAssign,
  AnyObject,
} from '@naturalcycles/js-lib'
import {
  ActivationChannel,
  AppId,
  ExternalAccountId,
  Goal,
  HardwareId,
  MixpanelGenericEventProps,
  ProductKey,
  ViewType,
} from '@naturalcycles/shared'
import { signupPages } from '@src/app/constants/pages.js'
import { getState } from '@src/app/core/store/signalStore.js'
import { botDetectionService, botReason, isCDP, isE2E } from '@src/app/core/util/e2e.util.js'
import { PageViewData } from '@src/app/shared/typings/analytics.js'
import { ManageAccountPage, Page } from '@src/app/shared/typings/enum/pages.js'
import { env } from '@src/environments/environment.js'
import mixpanel from 'mixpanel-browser'
import { logger } from '../../util/log.util.js'
import { getTrafficOrigin, TrafficOrigin } from '../../util/trafficOrigin.util.js'

Object.assign(globalThis, { mixpanel }) // debugging
let mp = mixpanel

const { mixpanelId } = env
const enabled = !!mixpanelId && !isE2E() && !botDetectionService.isBot()

if (!enabled) {
  // This "mocks" mixpanel methods as dummy methods
  mp = {
    identify() {},
    track() {},
    reset() {},
    get_distinct_id() {},
    get_property() {},
  } as any
} else {
  mp.init(mixpanelId, {
    // prevent redundant requests
    // https://help.mixpanel.com/hc/en-us/articles/115004568086-The-mp-page-view-Event
    // track_pageview: false,
    // https://help.mixpanel.com/hc/en-us/articles/115004615466-Disable-Autotrack
    disable_notifications: true,
    autotrack: false,
    // props to consider:
    // api_host: 'https://api-eu.mixpanel.com',
    cross_subdomain_cookie: env.prod,
    // ignore_dnt: true,
  })
}

@Injectable({ providedIn: 'root' })
export class MixpanelService {
  getDistinctId(): string | undefined {
    return mp.get_distinct_id()
  }

  getDeviceId(): string | undefined {
    return mp.get_property('$device_id')
  }

  trackEvent(eventName: string, properties: AnyObject = {}): void {
    properties = {
      ...this.getGenericProperties(),
      ...properties,
    }
    if (!env.prod) {
      logger.log(`[MixpanelService] ${eventName}`, properties)
    }
    mp.track(eventName, properties)
  }

  trackView(path: string, data: PageViewData): void {
    const pathName = this.addSignupPrefix(path)
    this.trackEvent('Viewed ' + pathName, data)
  }

  private isManageAccountPagePath(path: string): boolean {
    return path.includes(ManageAccountPage.account)
  }

  addSignupPrefix(path: string): string {
    // Add "/signup" as a prefix to the signup pages so that they do not have the same path name as the web.
    // Note that consent page path is hardcoded in auth component.
    const parts = path.split('/')
    const page = _last(parts) as Page

    if (signupPages.includes(page) && !this.isManageAccountPagePath(path)) {
      return `/signup${path}`
    }
    return path
  }

  identify(id: ExternalAccountId): void {
    mp.identify(id)
  }

  register(userId: ExternalAccountId, once = false): void {
    if (once) {
      mp.register_once({ userId })
    } else {
      mp.register({ userId })
    }
  }

  private getGenericProperties(): GenericEventProperties {
    const { account } = getState()
    const p: GenericEventProperties = {
      'view type': 'Website',
      repo: 'websignup',
      externalAccountId: account.externalAccountId,
      trafficOrigin: getTrafficOrigin(),
      botReason: botReason || undefined,
      cdp: isCDP || undefined,
      e2e: isE2E() || undefined,
    }

    if (account.id) {
      _objectAssign(
        p,
        _filterNullishValues({
          // Legacy, aya-baya!
          demo: account.demoMode,
          goal: Goal[account.goal!],
          plan: account.plan,
          // Modern
          'Account.demoMode': !!account.demoMode,
          'Account.goal': Goal[account.goal!],
          'Account.hwId': HardwareId[account.hwId],
          'Account.plan': account.plan,
          'Account.hasTherm': account.hasTherm,
          'Account.appId': _numberEnumKeyOrUndefined(AppId, account.appId),
          'Account.activationChannel': _numberEnumKeyOrUndefined(
            ActivationChannel,
            account.activationChannel,
          ),
          ...(window.Cookiebot && {
            'CookieConsent.statistics': window.Cookiebot.consent.statistics,
            'CookieConsent.marketing': window.Cookiebot.consent.marketing,
          }),
          complete: !!account.completeDate,
        }),
      )
    }

    return p
  }
}

interface GenericEventProperties extends MixpanelGenericEventProps {
  'view type': ViewType
  trafficOrigin: TrafficOrigin
  // Legacy
  demo?: boolean
  goal?: string
  plan?: ProductKey
  // Modern
  'Account.demoMode'?: boolean
  'Account.goal'?: string
  'Account.hwId'?: string
  'Account.plan'?: ProductKey
  'Account.hasTherm'?: boolean
  'Account.appId'?: string
  'Account.activationChannel'?: string
  'CookieConsent.statistics'?: boolean
  'CookieConsent.marketing'?: boolean
  complete?: boolean
}
